import React, { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router";
import { Spin } from "antd";
import { IndependentDynamicForm } from "@companion-core/shared/app/Components/DynamicForm/IndependentDynamicForm";
import { RequestDetailLayout } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailLayout";
import { RequestDetailBoxLayout } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox/RequestDetailBoxLayout";
import { PopIn } from "@companion-core/shared/app/Components/PopIn";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import {
  RequestDetailAction,
  RequestDetailContent,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import {
  DesktopEditorProps,
  MobileEditorProps,
  RequestEditorContentProps,
  RequestEditorHeaderProps,
  RequestEditorProps,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestEditor/requestEditor.model";
import { useRequestEditor } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestEditor/hooks/useRequestEditor";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import "@companion-core/web/src/Assets/Styles/scss/myRequestsEditor.scss";

const getEditorBackPath = (id: string, action: RequestDetailAction) =>
  `/my-requests/${id}/details/${
    action === RequestDetailAction.decline
      ? RequestDetailContent.recommendations
      : RequestDetailContent.detail
  }`;

export const RequestEditor = ({ requestData, id, action }: RequestEditorProps) => {
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const {
    t,
    isLoading,
    isPopInOpen,
    popInArgs,
    member,
    initialForm,
    sendModificationRequest,
    isRecommendationsLoading,
  } = useRequestEditor({
    requestData,
    action: action as RequestDetailAction,
    loader: <Spin size="large" />,
    onClosePopIn: () => navigate(`/my-requests/${id}/details/detail`),
  });

  const editorAction = action as RequestDetailAction;
  const backPath = useMemo(
    () => (id ? getEditorBackPath(id, editorAction) : ""),
    [id, editorAction]
  );
  const backAriaLabel = t("request.my-requests.detail.editor.back.accessibility-label");
  const submitAccessibilityLabel = t(
    `request.my-requests.detail.${editorAction}.submit.accessibility-label`
  );

  const HeaderSection = ({ id, action }: RequestEditorHeaderProps) => (
    <div data-testid="request-detail__editor--header" className="request-detail__editor-header">
      <Link
        to={getEditorBackPath(id ?? "", action as RequestDetailAction)}
        aria-label={backAriaLabel}
        data-testid="request-detail__editor--header-back-cta"
        className="request-detail__editor-header-button">
        <FontIcon name="JP-Arrow-left-bold" size={24} aria-hidden="true" />
      </Link>
      <div
        data-testid="request-detail__editor--header-close-cta"
        className="request-detail__editor-header-button"
        onClick={() => navigate(`/my-requests/${id}/details/detail`)}>
        <FontIcon name="JP-Close-bold" size={24} aria-hidden="true" />
      </div>
    </div>
  );

  const RequestEditorContent = ({
    action,
    name,
    initialForm,
    sendModificationRequest,
  }: RequestEditorContentProps) => (
    <div
      data-testid="request-detail__editor--container"
      className="request-detail__editor-container">
      <h3
        data-testid="request-detail__editor--title"
        className="request-detail__editor-title title-7">
        {t(`request.my-requests.detail.editor.${action}.title`, {
          name: name ?? "",
        })}
      </h3>
      {isLoading ? (
        <Spin
          size="default"
          className="request-detail__editor-loader request-detail__editor-loader--form"
        />
      ) : (
        <div
          data-testid="request-detail__editor--action-form"
          className="request-detail__editor-form">
          <IndependentDynamicForm
            name={`${action}-request`}
            initialFormJSON={initialForm}
            onSubmitForm={sendModificationRequest}
            titleTag="h3"
            submitButtonAccessibilityLabel={submitAccessibilityLabel}
          />
        </div>
      )}
    </div>
  );

  const MobileEditor = ({ action, initialForm, sendModificationRequest }: MobileEditorProps) => (
    <>
      <HeaderSection id={id} action={action} />
      <RequestEditorContent
        action={action}
        name={member?.firstname ?? ""}
        initialForm={initialForm}
        sendModificationRequest={sendModificationRequest}
      />
    </>
  );

  const DesktopEditor = ({
    requestData,
    action,
    family,
    initialForm,
    sendModificationRequest,
  }: DesktopEditorProps) => {
    const navigateToDetailContent = useCallback(
      (to: RequestDetailContent) => {
        if (id && to) {
          navigate(`/my-requests/${id}/details/${to}`, { state: { canGoBack: true } });
        }
      },
      [id]
    );

    return (
      <RequestDetailLayout data={requestData}>
        <RequestDetailBoxLayout
          title={t(`request.my-requests.edit.${action}.title`)}
          family={family}
          requestId={id ?? ""}
          hasDigitalProposal={false}
          proposalsData={{
            // eslint-disable-next-line camelcase
            data: { status: "success", error_message: null, results: null, code: 200 },
          }}
          isProposalsPending={false}
          performDisplayProposals={navigateToDetailContent.bind(
            null,
            RequestDetailContent.proposals
          )}
          performDisplayRecommendationDetail={() => {}}
          backHref={backPath}
          backAriaLabel={backAriaLabel}>
          <RequestEditorContent
            action={action}
            name={member?.firstname ?? ""}
            initialForm={initialForm}
            sendModificationRequest={sendModificationRequest}
          />
        </RequestDetailBoxLayout>
      </RequestDetailLayout>
    );
  };

  return requestData.status === "recommended" && isRecommendationsLoading ? (
    <Spin size="large" className="request-detail__editor-loader" />
  ) : (
    <div data-testid="request-detail__editor" className="request-detail__editor">
      {popInArgs && <PopIn isOpen={isPopInOpen} {...popInArgs} />}
      {isMobileSize ? (
        <MobileEditor
          action={action as RequestDetailAction}
          initialForm={initialForm}
          sendModificationRequest={sendModificationRequest}
        />
      ) : (
        <DesktopEditor
          requestData={requestData}
          action={action as RequestDetailAction}
          family={requestData.family ?? ""}
          initialForm={initialForm}
          sendModificationRequest={sendModificationRequest}
        />
      )}
    </div>
  );
};
